import { WSLabelProps } from "@wingspanhq/fe-component-library";
import {
  ICheckbookCard,
  IPaymentCard
} from "@wingspanhq/payments/dist/interfaces";
import {
  AccountStatus,
  IAccount,
  ICreditCard
} from "@wingspanhq/users/dist/lib/interfaces";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import capitalize from "lodash/capitalize";
import { useHistory } from "react-router-dom";
import type { bankingApiSpec } from "@wingspanhq/wingspan-api-types";
import {
  avatarBankAccount,
  avatarBankCard,
  avatarPlatformBalance,
  avatarTaxWithholding,
  avatarWallet
} from "../../../../shared/constants/avatars";
import { creditCardBrandNameMap } from "../../../../shared/constants/bankCard";
import { selectorAccountInstitution } from "../../../../shared/selectors/selectorAccountInstitution";
import { selectorAccountName } from "../../../../shared/selectors/selectorAccountName";
import { selectorAccountNumber } from "../../../../shared/selectors/selectorAccountNumber";
import { selectorAccountVerificationPending } from "../../../../shared/selectors/selectorAccountVerificationPending";
import { selectorDebitCardBrand } from "../../../../shared/selectors/selectorDebitCardBrand";
import { PATH_PLATFORM_BALANCE } from "../../../PlatformBalance/paths";
import { PATH_TAXES } from "../../../Taxes/paths";
import { ListItemProps, MethodActionHandlers, MethodsData } from "./types";

export function getAccountLabelProps(account: IAccount): WSLabelProps {
  return {
    text: selectorAccountInstitution(account),
    secondaryText: selectorAccountName(account),
    avatar: avatarBankAccount
  };
}

export function getInternalAccountLabelProps(
  internalAccount: bankingApiSpec.InternalAccount,
  historyPush?: ReturnType<typeof useHistory>["push"],
  balance?: number
): WSLabelProps {
  const mask = internalAccount.numbers?.account?.slice(-4);
  const maskText = mask ? `(${mask})` : undefined;
  const balanceText =
    balance !== undefined
      ? `${formatMoney(balance, internalAccount.currency)} is available`
      : undefined;

  switch (internalAccount.type) {
    case "TaxWithholding":
      return {
        link: {
          label: "Tax Withholding",
          onClick: historyPush
            ? () => {
                historyPush(PATH_TAXES);
              }
            : undefined
        },
        secondaryText: maskText,
        helperText: balanceText,
        avatar: avatarTaxWithholding
      };

    case "ApPayouts":
      return {
        link: {
          label: `${internalAccount.currency} Platform balance`,
          onClick: historyPush
            ? () => {
                historyPush(PATH_PLATFORM_BALANCE);
              }
            : undefined
        },
        helperText: balanceText,
        avatar: avatarPlatformBalance
      };

    default:
      return {
        link: {
          label: "Wingspan Wallet",
          onClick: historyPush
            ? () => {
                historyPush("/member/wallet");
              }
            : undefined
        },
        secondaryText: maskText,
        helperText: balanceText,
        avatar: avatarWallet
      };
  }
}

// Card expiration is exposed differently across the three card models
// (a single "MM/YY" string on credit cards, split MM + YYYY on checkbook
// and payment cards). Normalize them all to a compact "Exp MM/YY" label.
const formatCardExpiration = (
  month?: string | number,
  year?: string | number
): string | undefined => {
  if (
    month === undefined ||
    month === null ||
    month === "" ||
    year === undefined ||
    year === null ||
    year === ""
  ) {
    return undefined;
  }

  const mm = String(month).padStart(2, "0");
  const yy = String(year).slice(-2);

  return `Exp ${mm}/${yy}`;
};

export function getCreditCardLabelProps(creditCard: ICreditCard): WSLabelProps {
  return {
    text: creditCardBrandNameMap[creditCard.cardBrand || ""] || "Credit Card",
    secondaryText: creditCard.last4 ? `(${creditCard.last4})` : undefined,
    helperText: creditCard.expiryDate
      ? `Exp ${creditCard.expiryDate}`
      : undefined,
    avatar: avatarBankCard
  };
}

export function getCheckbookCardLabelProps(
  checkbookCard: ICheckbookCard
): WSLabelProps {
  return {
    text: selectorDebitCardBrand(checkbookCard),
    secondaryText: checkbookCard.last4 ? `(${checkbookCard.last4})` : undefined,
    helperText: formatCardExpiration(
      checkbookCard.expirationMM,
      checkbookCard.expirationYYYY
    ),
    avatar: avatarBankCard
  };
}

export function getPaymentCardLabelProps(
  paymentCard: IPaymentCard
): WSLabelProps {
  return {
    text: capitalize(paymentCard.brand),
    secondaryText: paymentCard.mask ? `(${paymentCard.mask})` : undefined,
    helperText: formatCardExpiration(
      paymentCard.expirationMonth,
      paymentCard.expirationYear
    ),
    avatar: avatarBankCard
  };
}

export function getMethods(
  data: MethodsData,
  handlers?: MethodActionHandlers,
  category?: string
) {
  const items: ListItemProps[] = [];

  data.accounts?.forEach(account => {
    const microdepositsVerificationPending =
      selectorAccountVerificationPending(account);

    const hasNoNumber = !selectorAccountNumber(account);

    items.push({
      id: account.accountId,
      type: "Account",
      label: getAccountLabelProps(account),
      pill: hasNoNumber
        ? {
            theme: "warning",
            icon: true,
            text: "Missing information"
          }
        : undefined,
      country: account.country || "US",
      category,
      actions: {
        select: hasNoNumber
          ? undefined
          : () => {
              handlers?.handleChange?.({
                id: account.accountId,
                type: "Account",
                category
              });
            },

        blocked: hasNoNumber
          ? () => handlers?.notifyAccountSelectionBlocked?.()
          : undefined,

        remove: () => {
          handlers?.openModalRemove?.({
            id: account.accountId,
            type: "Account",
            category
          });
        },

        resync: hasNoNumber ? () => handlers?.openPlaid!() : undefined,

        reconnect:
          account.status === AccountStatus.Disconnected &&
          account.publicToken &&
          !!handlers?.openPlaidReconnect
            ? () => {
                handlers?.openPlaidReconnect!(account);
              }
            : undefined,

        verify:
          microdepositsVerificationPending &&
          !!handlers?.openMicrodepositsVerify
            ? () => {
                handlers?.openMicrodepositsVerify!({ account });
              }
            : undefined
      }
    });
  });

  data.internalAccounts?.forEach(internalAccount => {
    items.push({
      id: internalAccount.internalAccountId,
      type: "InternalAccount",
      label: getInternalAccountLabelProps(
        internalAccount,
        handlers?.historyPush
      ),
      country: internalAccount.type === "ApPayouts" ? "Platform" : "US",
      category,
      actions: {
        select: () => {
          handlers?.handleChange?.({
            id: internalAccount.internalAccountId,
            type: "InternalAccount",
            category
          });
        }
      }
    });
  });

  data.creditCards?.forEach(creditCard => {
    items.push({
      id: creditCard.paymentMethodId || "",
      type: "CreditCard",
      label: getCreditCardLabelProps(creditCard),
      country: "US",
      category,
      actions: {
        select: () => {
          handlers?.handleChange?.({
            id: creditCard.paymentMethodId || "",
            type: "CreditCard",
            category
          });
        },
        remove: () => {
          handlers?.openModalRemove?.({
            id: creditCard.paymentMethodId || "",
            type: "CreditCard",
            category
          });
        }
      }
    });
  });

  data.checkbookCards?.forEach(checkbookCard => {
    items.push({
      id: checkbookCard.cardId,
      type: "CheckbookCard",
      label: getCheckbookCardLabelProps(checkbookCard),
      country: "US",
      category,
      actions: {
        select: () => {
          handlers?.handleChange?.({
            id: checkbookCard.cardId,
            type: "CheckbookCard",
            category
          });
        },
        remove: () => {
          handlers?.openModalRemove?.({
            id: checkbookCard.cardId,
            type: "CheckbookCard",
            category
          });
        }
      }
    });
  });

  data.paymentCards?.forEach(paymentCard => {
    items.push({
      id: paymentCard.paymentCardId,
      type: "PaymentCard",
      label: getPaymentCardLabelProps(paymentCard),
      country: paymentCard.country || "US",
      category,
      actions: {
        select: () => {
          handlers?.handleChange?.({
            id: paymentCard.paymentCardId,
            type: "PaymentCard",
            category
          });
        },
        remove: () => {
          handlers?.openModalRemove?.({
            id: paymentCard.paymentCardId,
            type: "PaymentCard",
            category
          });
        }
      }
    });
  });

  const methodsByCountry: Record<string, typeof items> = {};

  items.forEach(item => {
    if (!methodsByCountry[item.country]) {
      methodsByCountry[item.country] = [];
    }

    methodsByCountry[item.country].push(item);
  });

  // Convert object to array
  const methodsByCountryArray = Object.entries(methodsByCountry).map(
    ([country, methods]) => ({
      country,
      methods
    })
  );

  methodsByCountryArray.sort((a, b) => {
    if (a.country === "Platform" || a.country === "US") {
      return -1;
    }

    if (b.country === "Platform" || b.country === "US") {
      return 1;
    }

    return a.country.localeCompare(b.country);
  });

  return methodsByCountryArray;
}
